import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap, map, take as takeOp } from 'rxjs/operators';

import { VALIDATORS } from 'app/constants';
import { AppArticle as Article, AppLooseArticle as LooseArticle } from 'graphql/models/extended';
import {
  Account,
  AccountType,
  ArticleCreateInput,
  ArticleUpdateInput,
  Branch,
  Category,
  Condition,
  ContactPerson,
  ContactPersonCreateInput,
  ContactPersonUpdateInput,
  Customer,
  CustomerCreateInput,
  CustomerUpdateInput,
  DocumentVersionInput,
  ObjectCreateInput,
  ObjectUpdateInput,
  Object as Objekt,
  OrganizationUpdateInput,
  SortOrder,
  TemplateCreateInput,
  TemplateUpdateInput
} from 'graphql/models/generated';
import { UpdateOrganizationMutation } from 'graphql/mutations/update-organization';
import { OrganizationQuery } from 'graphql/queries/organization';
import { AccountTypeENUM, Manufacturer } from 'models/account.model';
import { DataItem, DataResourceType } from 'models/data.model';
import { Organization } from 'models/organization.model';
import { Template, TemplateType } from 'models/template.model';
import { CoreDataBuilderService } from 'shared/services/core-data-builder.service';
import { ProductService } from 'shared/services/product.service';
import { StoreService } from 'shared/services/store.service';
import { Apollo, gql } from 'apollo-angular';

export interface GetAccountsOptions {
  accountType?: AccountType;
  filters?: Record<string, string>;
  orderBy?: Record<string, any>[];
  searchTerm?: string;
  skip?: number;
  take?: number;
}

export interface GetItemsByResourceTypeOptions {
  accountType?: AccountType;
  filters?: Record<string, any>;
  id: string;
  order?: Record<string, any>[];
  searchNameOnly?: boolean;
  searchTerm?: string;
  skip: number;
  take: number;
  type: DataResourceType;
}

@Injectable({
  providedIn: 'root'
})
export class CoreDataService {
  isManufacturer!: boolean;

  accountOrgId?: string;

  constructor(
    private builderService: CoreDataBuilderService,
    private productService: ProductService,
    private organizationQuery: OrganizationQuery,
    private updateOrganizationMutation: UpdateOrganizationMutation,
    private storeService: StoreService,
    private apollo: Apollo
  ) {
    this.init();
  }

  private init(): void {
    this.storeService.getAccount().subscribe(acc => {
      this.accountOrgId = acc.organization;
    })
    this.storeService.isCurrentAccountDealer().subscribe((isDealer: boolean) => {
      this.isManufacturer = !isDealer;
    });
  }

  getItemsByResourceType(options: GetItemsByResourceTypeOptions): Observable<DataItem[]> {
    if (options === null || typeof options !== 'object') {
      throw new TypeError('Expected an options object');
    }

    const { accountType, filters, id, order, searchNameOnly, searchTerm, skip, take, type } = options;

    const orderBy: Record<string, any>[] | undefined = order || undefined;

    switch (type) {
      case 'Customer':
        return this.getCustomers(take, skip, orderBy, searchTerm, searchNameOnly, accountType);
      case 'ContactPerson':
        return this.getContactPersons(id, take, skip, orderBy, searchTerm);
      case 'Object':
        return this.getObjects(take, skip, orderBy, searchTerm, searchNameOnly);
      case 'Condition':
        return this.getCustomersWithCondition(take, skip, orderBy, searchTerm, accountType);
      case 'CustomersWithoutCondition':
        return this.getCustomersWithoutCondition(take, skip, orderBy, searchTerm, accountType);
      case 'Article':
        return this.getArticles(take, skip, orderBy, searchTerm, filters);
      case 'LooseArticle':
        return this.getLooseArticles(take, skip, id, searchTerm);
      case 'Account':
        return this.getAccounts({
          take,
          skip,
          orderBy,
          searchTerm,
          filters,
          accountType
        });
      case 'Branch':
        return this.getBranches(take, skip, orderBy, searchTerm);
      default:
        return of([]);
    }
  }

  getCustomer(id: string): Observable<Customer> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Customer>('Customer', variables);
  }

  getCustomers(
    take = 10,
    skip = 0,
    orderBy?: Record<string, any>[],
    searchTerm?: string,
    searchNameOnly?: boolean,
    accountType?: AccountType
  ): Observable<Customer[]> {
    // eslint-disable-next-line @typescript-eslint/ban-types
    let searchConditions: Array<object> = [{ name: { contains: searchTerm } }];

    if (!searchNameOnly) {
      searchConditions = [
        ...searchConditions,
        { customerNumber: { contains: searchTerm } },
        ...(searchTerm ? [{ additionalNames: { hasSome: [searchTerm] } }] : [])
      ];

      if (accountType !== AccountTypeENUM.DEALER) {
        searchConditions = [
          ...searchConditions,
          { id: { contains: searchTerm } }
        ];
      }
    }

    const where = { OR: searchConditions, AND: { missingInSource: { equals: false } } };

    const variables = {
      take,
      skip,
      orderBy,
      where
    };
    return this.builderService.getMany<Customer>('Customer', variables);
  }

  /** Returns the conditions for the current customer, or if an `id` is given for the specific customer.
   * If the customer should use default conditions, the organization default conditions are returned.
   */
  getCustomerConditions(id?: string): Observable<Condition> {
    return (id ? this.getCustomer(id) : this.storeService.getCurrentCustomer()).pipe(
      concatMap((customer: Customer) =>
        customer.conditions && customer.useCustomConditions
          ? of({ ...customer.conditions, vat: customer.vat })
          : this.getOrganizationDefaultConditions()
      )
    );
  }

  getContactPerson(id: string): Observable<ContactPerson> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<ContactPerson>('ContactPerson', variables);
  }

  getContactPersons(
    id: string,
    take = 10,
    skip = 0,
    orderBy?: Record<string, any>[],
    searchTerm?: string
  ): Observable<ContactPerson[]> {
    const where = {
      customers: { some: { id: { equals: id } } },
      OR: [{ firstName: { contains: searchTerm } }, { lastName: { contains: searchTerm } }]
    };
    const variables = {
      take,
      skip,
      orderBy,
      where
    };

    return this.builderService.getMany<ContactPerson>('ContactPerson', variables);
  }

  getObject(id: string): Observable<Objekt> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Objekt>('Object', variables);
  }

  getObjects(
    take = 10,
    skip = 0,
    orderBy?: Record<string, any>[],
    searchTerm?: string,
    searchNameOnly?: boolean
  ): Observable<Objekt[]> {
    // eslint-disable-next-line @typescript-eslint/ban-types
    let searchConditions: Array<object> = [{ name: { contains: searchTerm } }];

    if (!searchNameOnly) {
      searchConditions = [
        ...searchConditions,
        {
          OR: [
            {
              AND: [{ objectNumber: { equals: null } }, { id: { contains: searchTerm } }]
            },
            { objectNumber: { contains: searchTerm } }
          ]
        }
      ];
    }

    const where = { AND: [{createdByManufacturer: { equals: this.isManufacturer }}, { OR: searchConditions }] };

    const variables = {
      take,
      skip,
      orderBy,
      where
    };

    return this.builderService.getMany<Objekt>('Object', variables);
  }

  getObjectByNumber(objectNumber: string, take = 10, skip = 0): Observable<Objekt[]> {
    const where = {
      OR: [
        {
          AND: [{ objectNumber: { equals: null } }, { id: { equals: objectNumber } }]
        },
        { objectNumber: { equals: objectNumber } }
      ]
    };

    const variables = {
      take,
      skip,
      where
    };

    return this.builderService.getMany<Objekt>('Object', variables);
  }

  searchObjectsByNumber(objectNumber: string, take = 10, skip = 0): Observable<Objekt[]> {
    const where = {
      OR: [
        {
          AND: [{ objectNumber: { equals: null } }, { id: { startsWith: objectNumber } }]
        },
        { objectNumber: { startsWith: objectNumber } }
      ]
    };

    const variables = {
      take,
      skip,
      where
    };

    return this.builderService.getMany<Objekt>('Object', variables);
  }

  getCustomersWithCondition(
    take = 10,
    skip = 0,
    orderBy?: Record<string, any>[],
    searchTerm?: string,
    accountType?: AccountType
  ): Observable<Customer[]> {
    let searchConditions: Array<object> = [
      { name: { contains: searchTerm } },
      { customerNumber: { contains: searchTerm } }
    ];

    if (accountType !== AccountTypeENUM.DEALER) {
      searchConditions = [
        ...searchConditions,
        { id: { contains: searchTerm } }
      ];
    }

    const where = {
      NOT: [{ conditions: null }],
      OR: searchConditions
    };
    const variables = {
      take,
      skip,
      orderBy,
      where
    };

    return this.builderService.getMany<Customer>('Customer', variables, 'CustomerWithConditionsFragment');
  }

  getCustomersWithoutCondition(
    take = 10,
    skip = 0,
    orderBy?: Record<string, any>[],
    searchTerm?: string,
    accountType?: AccountType
  ): Observable<Customer[]> {
    let searchConditions: Array<object> = [
      { name: { contains: searchTerm } },
      { customerNumber: { contains: searchTerm } }
    ];

    if (accountType !== AccountTypeENUM.DEALER) {
      searchConditions = [
        ...searchConditions,
        { id: { contains: searchTerm } }
      ];
    }

    const where = {
      NOT: [{ conditions: null }],
      OR: searchConditions
    };
    const variables = {
      take,
      skip,
      orderBy,
      where
    };

    return this.builderService.getMany<Customer>('Customer', variables, 'CustomerWithoutConditionsFragment');
  }

  getArticleCategories(): Observable<Category[]> {
    const query = gql`
      query {
        categoriesWithArticlesOnlyFromOrganization(organizationId: "${this.accountOrgId}") {
          id
          name
          displayName
        }
      }
    `;

    return this.apollo
      .query({
        query,
        variables: {},
        fetchPolicy: 'no-cache'
      })
      .pipe(
        map((r) => (r as any).data?.categoriesWithArticlesOnlyFromOrganization as Category[]),
        takeOp(1));
  }

  getArticle(id: string): Observable<Article> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Article>('Article', variables);
  }

  getArticles(
    take = 10,
    skip = 0,
    orderBy?: Record<string, any>[],
    searchTerm?: string,
    filters?: Record<string, any>
  ): Observable<Article[]> {
    let where: Record<string, any> = {
      OR: [{ name: { contains: searchTerm } }, { ean: { contains: searchTerm } }, { mnr: { contains: searchTerm } }]
    };
    if (filters) {
      const whereAnd: Record<string, any>[] = [];

      if (filters.categories && filters.categories.length) {
        whereAnd.push({ category: { displayName: { in: filters.categories } } });
      }
      if (filters.listPriceMin) {
        whereAnd.push({ listPrice: { gte: Number(filters.listPriceMin) } });
      }
      if (filters.listPriceMax) {
        whereAnd.push({ listPrice: { lte: Number(filters.listPriceMax) } });
      }

      if (filters.purchasePriceMin) {
        whereAnd.push({ purchasePrice: { gte: Number(filters.purchasePriceMin) } });
      }
      if (filters.purchasePriceMax) {
        whereAnd.push({ purchasePrice: { lte: Number(filters.purchasePriceMax) } });
      }

      if (whereAnd.length) {
        whereAnd.push({ ...where });
        where = { AND: whereAnd };
      }
    }
    const variables = {
      take,
      skip,
      orderBy,
      where
    };

    return this.builderService.getMany<Article>('Article', variables, 'ArticleFragment');
  }

  getLooseArticles(take = 10, skip = 0, categoryId: string, searchTerm?: string): Observable<LooseArticle[]> {
    return this.productService.getProductsList(take, skip, categoryId, searchTerm);
  }

  getAccounts(options: GetAccountsOptions): Observable<Account[]> {
    if (options === null || typeof options !== 'object') {
      // eslint-disable-next-line no-param-reassign
      options = {};
    }

    let {
      // eslint-disable-next-line prefer-const
      accountType,
      filters,
      orderBy,
      searchTerm,
      skip,
      take
    } = options;

    if (typeof take !== 'number') {
      take = 10;
    } else if (take < 0) {
      take = 0;
    }

    if (typeof skip !== 'number' || skip < 0) {
      skip = 0;
    }

    const where: Record<string, any> = {
      OR: [
        { profile: { firstName: { contains: searchTerm } } },
        { profile: { lastName: { contains: searchTerm } } },
        { username: { contains: searchTerm } }
      ]
    };

    if (accountType) {
      where.accountType = { equals: accountType };
    }

    let variables = {
      take,
      skip,
      orderBy,
      where
    };

    if (
      filters &&
      filters.branch &&
      filters.branch !== 'ALL_BRANCHES' &&
      accountType !== AccountTypeENUM.MANUFACTURER
    ) {
      variables = Object.assign(variables, {
        whereBranch: { id: { equals: filters.branch } },
        branchOperator: 'AND'
      });
    }

    return this.builderService.getMany<Account>('Account', variables);
  }

  /** Lightweight account lookup for dropdowns/pickers: fetches names only,
   * via the `accountNames` query which skips the auth-server status lookups. */
  getAccountNames(options: GetAccountsOptions): Observable<Account[]> {
    if (options === null || typeof options !== 'object') {
      // eslint-disable-next-line no-param-reassign
      options = {};
    }

    let {
      // eslint-disable-next-line prefer-const
      accountType,
      filters,
      orderBy,
      searchTerm,
      skip,
      take
    } = options;

    if (typeof take !== 'number') {
      take = 10;
    } else if (take < 0) {
      take = 0;
    }

    if (typeof skip !== 'number' || skip < 0) {
      skip = 0;
    }

    const where: Record<string, any> = {
      OR: [
        { profile: { firstName: { contains: searchTerm } } },
        { profile: { lastName: { contains: searchTerm } } },
        { username: { contains: searchTerm } }
      ]
    };

    if (accountType) {
      where.accountType = { equals: accountType };
    }

    if (
      filters &&
      filters.branch &&
      filters.branch !== 'ALL_BRANCHES' &&
      accountType !== AccountTypeENUM.MANUFACTURER
    ) {
      // same filter the gateway derives from whereBranch for the accounts query
      where.accountRoles = { branchRoles: { some: { branchId: { equals: filters.branch } } } };
    }

    const query = gql`
      query accountNames($take: Int, $skip: Int, $orderBy: [AccountOrderByInput!], $where: AccountWhereInput) {
        accountNames(take: $take, skip: $skip, orderBy: $orderBy, where: $where) {
          id
          authId
          username
          profile {
            firstName
            lastName
          }
        }
      }
    `;

    return this.apollo
      .query({
        query,
        variables: {
          take,
          skip,
          orderBy,
          where
        },
        fetchPolicy: 'no-cache'
      })
      .pipe(
        map((r) => (r as any).data?.accountNames as Account[]),
        takeOp(1));
  }

  getBranches(take: number, skip: number, orderBy?: Record<string, any>[], searchTerm?: string): Observable<Branch[]> {
    const where = {
      OR: [
        { name: { contains: searchTerm } },
        {
          address: {
            OR: [
              { city: { contains: searchTerm } },
              { zipCode: { contains: searchTerm } },
              { streetNr: { contains: searchTerm } },
              { streetName: { contains: searchTerm } }
            ]
          }
        }
      ]
    };
    const variables = {
      take,
      skip,
      orderBy,
      where
    };

    return this.builderService.getMany<Branch>('Branch', variables);
  }

  getTemplates(
    take = 10,
    type: TemplateType,
    branchId?: string,
    orderBy?: Record<string, SortOrder>[]
  ): Observable<Template[]> {
    const where = { type: { equals: type } } as Record<string, any>;
    const variables = { take, orderBy, where };

    if (type === TemplateType.OFFER) {
      if (branchId) {
        where.OR = [{ branchId: { equals: null } }, { branchId: { equals: branchId } }];
      } else {
        where.branchId = { equals: null };
      }
    }

    return this.builderService.getMany<Template>('Template', variables);
  }

  deleteConditionForCustomer(id: string): Observable<Customer> {
    const data = {
      useCustomConditions: { set: false },
      conditions: { delete: true }
    };

    return this.builderService.update<Customer, CustomerUpdateInput>('Customer', id, data);
  }

  createObject(formValue: Record<string, any>, documentVersion?: DocumentVersionInput): Observable<Objekt> {
    const values = { ...formValue };
    if (!values.additionalAddresses.length) {
      delete values.additionalAddresses;
    }

    if (values.objectNumber === '') {
      delete values.objectNumber;
    }

    return this.builderService.create<Objekt, ObjectCreateInput>('Object', values, documentVersion);
  }

  createCustomer(formValue: Record<string, any>, documentVersion?: DocumentVersionInput): Observable<Customer> {
    const formValues = this.mapCustomerValues(formValue);
    formValues.createdManually = true;
    return this.builderService.create<Customer, CustomerCreateInput>('Customer', formValues, documentVersion);
  }

  createContactPerson(
    formValue: Record<string, any>,
    customerId?: string,
    documentVersion?: DocumentVersionInput
  ): Observable<ContactPerson> {
    const values = { ...formValue };
    if (customerId) {
      values.customers = { connect: [{ id: customerId }] };
    }

    return this.builderService.create<ContactPerson, ContactPersonCreateInput>(
      'ContactPerson',
      values,
      documentVersion
    );
  }

  createTemplate(formValue: Record<string, any>): Observable<Template> {
    return this.builderService.create<Template, TemplateCreateInput>('Template', formValue);
  }

  updateTemplate(id: string, formValue: Record<string, any>): Observable<Template> {
    const data: TemplateUpdateInput = this.builderService.buildDataInput(formValue, false);

    return this.builderService.update<Template, TemplateUpdateInput>('Template', id, data);
  }

  deleteTemplate(id: string): Observable<Template> {
    return this.builderService.delete<Template>('Template', id);
  }

  createArticle(formValue: Record<string, any>, manufacturer: Manufacturer): Observable<Article> {
    const values: Record<string, any> = this.mapArticleValues(formValue, manufacturer);

    return this.builderService.create<Article, ArticleCreateInput>('Article', values);
  }

  updateArticle(id: string, formValue: Record<string, any>, manufacturer: Manufacturer): Observable<Article> {
    const values: Record<string, any> = this.mapArticleValues(formValue, manufacturer);
    const data: ArticleUpdateInput = this.builderService.buildDataInput(values, false);

    if (!values.category && values.originalCategory) {
      data.category = {
        disconnect: true
      };
    }

    delete (data as any).originalCategory;
    return this.builderService.update<Article, ArticleUpdateInput>('Article', id, data);
  }

  deleteArticle(id: string): Observable<Article> {
    return this.builderService.delete<Article>('Article', id);
  }

  updateContactPerson(id: string, formValue: Record<string, any>): Observable<ContactPerson> {
    const data: ContactPersonUpdateInput = this.builderService.buildDataInput(formValue, false);

    return this.builderService.update<ContactPerson, ContactPersonUpdateInput>('ContactPerson', id, data);
  }

  deleteContactPerson(id: string): Observable<ContactPerson> {
    return this.builderService.delete<ContactPerson>('ContactPerson', id);
  }

  updateCustomer(id: string, formValue: Record<string, any>): Observable<Customer> {
    let values: Record<string, any>;

    if (this.isManufacturer) {
      const allowed = ['information', 'vat'];
      const allowedKeys = Object.keys(formValue).filter((key) => allowed.includes(key));
      if (allowedKeys.length === 0) {
        return this.storeService.getCurrentCustomer();
      }

      values = {};
      allowedKeys.forEach((key) => {
        values[key] = formValue[key];
      });

      values = this.mapCustomerValues(values);
    } else {
      values = this.mapCustomerValues(formValue);
    }

    const data: CustomerUpdateInput = this.builderService.buildDataInput(values, false);

    if (data.conditions && data.conditions.update) {
      const update: any = data.conditions.update;
      const craate: any = {};
      Object.keys(update).forEach((key) => {
        craate[key] = update[key].set;
      });
      const conditions: any = {
        upsert: {
          update,
          create: craate
        }
      };
      data.conditions = conditions;
    }

    return this.builderService.update<Customer, CustomerUpdateInput>('Customer', id, data);
  }

  deleteCustomer(id: string): Observable<Customer> {
    return this.builderService.delete<Customer>('Customer', id);
  }

  updateObject(id: string, formValue: Record<string, any>): Observable<Objekt> {
    const values = { ...formValue };
    if (values.objectNumber === '') {
      values.objectNumber = id;
    }
    const data: ObjectUpdateInput = this.builderService.buildDataInput(values, false);

    return this.builderService.update<Objekt, ObjectUpdateInput>('Object', id, data);
  }

  deleteObject(id: string): Observable<Objekt> {
    return this.builderService.delete<Objekt>('Object', id);
  }

  private mapCustomerValues(formValues: Record<string, any>): Record<string, any> {
    const values = { ...formValues };

    if (values.additionalNames !== undefined) {
      values.additionalNames = values.additionalNames.map((n: { name: string }) => n.name);
    }
    if (values.conditions !== undefined) {
      // convert input strings to numbers
      values.conditions.vat = Number(values.conditions.vat);
      values.conditions.discount = Number(values.conditions.discount);
      values.conditions.surcharge = Number(values.conditions.surcharge);
    }
    if (values.useDefaultConditions !== undefined) {
      values.useCustomConditions = !values.useDefaultConditions;
      // remove default conditions field,
      // because the gateway will throw an error for non-existing field
      delete values.useDefaultConditions;
      if (!values.useCustomConditions) {
        delete values.conditions;
      }
    }

    return values;
  }

  getOrganizationDefaultConditions(): Observable<Condition> {
    return this.getOrganization().pipe<Organization | undefined, Condition>(
      takeOp(1),
      map((r) => (r as Organization).defaultConditions)
    );
  }

  getOrganization(): Observable<Organization | undefined> {
    return this.storeService
      .getAccountOrganization()
      .pipe(
        concatMap((id) =>
          this.organizationQuery
            .fetch({ id }, { fetchPolicy: 'no-cache' })
            .pipe(map((r) => r.data?.organization as Organization | undefined))
        )
      );
  }

  updateOrganization(formValue: Record<string, any>): Observable<any> {
    const values = this.mapOrganizationValues(formValue);
    const data = this.builderService.buildDataInput<OrganizationUpdateInput>(values, false);

    return this.storeService
      .getAccountOrganization()
      .pipe(
        concatMap((id: string) =>
          this.updateOrganizationMutation
            .mutate({ id, data }, { fetchPolicy: 'no-cache' })
            .pipe<Organization>(map((r) => r.data?.updateOrganization as Organization))
        )
      );
  }

  private mapOrganizationValues(formValues: Record<string, any>): Record<string, any> {
    const values = { ...formValues };

    values.defaultConditions.vat = Number(values.defaultConditions.vat);
    values.defaultConditions.surcharge = Number(values.defaultConditions.surcharge);
    values.defaultConditions.discount = Number(values.defaultConditions.discount);

    return values;
  }

  private mapArticleValues(formValues: Record<string, any>, manufacturer: Manufacturer): Record<string, any> {
    const values: Record<string, any> = { ...formValues };
    values.listPrice = values.listPrice ? Number(values.listPrice) : VALIDATORS.MIN.ARTICLE_LIST_PRICE;
    values.purchasePrice = values.purchasePrice ? Number(values.purchasePrice) : VALIDATORS.MIN.ARTICLE_PURCHASE_PRICE;
    if (values.category) {
      values.category = { name: values.category, displayName: values.category, manufacturer };
    } else {
      // fix "update article" request with empty category field
      delete values.category;
    }

    return values;
  }
}
